<template>
  <BaseCenteredModal
    v-model:is-modal-open="localOpen"
    :title="$trans(localColumn?.label)"
    :click-outside-close="false"
  >
    <template #footer>
      <BaseButton
        v-if="!toDelete"
        :title="$t('button.save')"
        icon="paper-plane"
        size="medium"
        color="primary"
        @click="saveData"
      />

      <BaseButton
        :data-tint="'danger'"
        :title="toDelete ? $t('button.confirm') : $t('button.delete')"
        color="danger"
        icon="trash"
        size="medium"
        @click="deleteColumn"
      />

      <BaseButton
        :title="$t('button.cancel')"
        icon="times"
        color="warning"
        size="medium"
        @click="cancelValue"
      />
    </template>

    <template #body>
      <div class="flex w-full flex-col gap-y-4 py-3">
        <div
          v-if="toDelete"
          class="flex w-full flex-col gap-y-4 text-xl first-letter:uppercase"
        >
          {{ $t('attributes.delete_column_confirmation_explain') }}
        </div>

        <div
          v-if="
            !toDelete && localAvailableFields && localAvailableFields.length > 0
          "
          class="flex w-full flex-row items-center gap-x-4"
        >
          <SelectComponent
            v-model="localColumn.field"
            :label="$t('button.selectField')"
            name="column_field"
            class="block w-full"
            :edition-mode="true"
            :multiple="localColumn.type === 'progressbar'"
            :searchable="true"
            :is-grouped="localColumn.type === 'progressbar'"
            group-key="group"
            :values="localAvailableFields"
            @change="onFieldChange"
          />
        </div>

        <div v-if="!toDelete" class="block w-full">
          <BaseShowEditTranslation
            v-model:translations="localColumn.label"
            :label="$t('attributes.name')"
            :edition-mode="true"
          />
        </div>

        <div
          v-if="!toDelete && (isSumAble || searchAble)"
          class="flex w-full flex-row items-center gap-x-4"
        >
          <BaseCheckBox
            v-if="isSumAble"
            v-model="localColumn.sum_able"
            :label="$t('button.allow_sum')"
            :class="'mb-0 capitalize'"
            :edition-mode="true"
            :no-margin-top="true"
            :size="'large'"
          />

          <BaseCheckBox
            v-if="searchAble"
            v-model="localColumn.search.searchable"
            :label="$t('button.allow_search')"
            :class="'mb-0 capitalize'"
            :edition-mode="true"
            :no-margin-top="true"
            :size="'large'"
          />
        </div>

        <div
          v-if="!toDelete && resizeAble"
          class="flex w-full flex-row items-center gap-x-4"
        >
          <BaseCheckBox
            v-model="widthAuto"
            :label="$t('attributes.width_auto')"
            :class="'mb-0 capitalize'"
            :edition-mode="true"
            :no-margin-top="true"
            :size="'large'"
          />

          <BaseInput
            v-model="widthValue"
            name="item_top"
            :label="$t('attributes.width')"
            :type="'number'"
            :parse-type="'float'"
            :min="50"
          />
        </div>
      </div>
    </template>
  </BaseCenteredModal>
</template>

<script>
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import BaseCenteredModal from '@c/addf-package/components/modal/BaseCenteredModal.vue'
import BaseShowEditTranslation from '@c/addf-package/components/BaseShowEditInput/BaseShowEditTranslation.vue'
import BaseCheckBox from '@c/addf-package/components/BaseCheckBox/BaseCheckBox.vue'
import BaseInput from '@c/addf-package/components/BaseInput/BaseInput.vue'
import SelectComponent from '@c/BaseFormComponent/SelectComponent.vue'

export default {
  name: 'ColumnManageModal',
  components: {
    SelectComponent,
    BaseInput,
    BaseCheckBox,
    BaseShowEditTranslation,
    BaseCenteredModal,
    BaseButton
  },
  props: {
    column: {
      type: Object,
      required: false,
      default: null
    },
    availableFields: {
      type: Object,
      required: true
    },
    open: {
      type: Boolean,
      required: true
    },
    currentWidth: {
      type: Number,
      required: true,
      default: null
    },
    resizeAble: {
      type: Boolean,
      required: false,
      default: false
    },
    isLast: {
      type: Boolean,
      required: false,
      default: false
    },
    sumAble: {
      type: Boolean,
      required: false,
      default: false
    },
    searchAble: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['update:column', 'update:open', 'update:dashboard', 'delete:column'],
  data() {
    return {
      oldLabel: {},
      oldWidth: 0,
      widthAuto: false,
      reloadDossier: false,
      toDelete: false
    }
  },
  computed: {
    isLocalHost() {
      return (
        location.host.includes('localhost') ||
        !location.protocol.includes('https')
      )
    },
    isSumAble() {
      return (
        this.sumAble &&
        ['currency'].includes(this.localColumn.type?.toLowerCase())
      )
    },
    localColumn: {
      get() {
        return this.column
      },
      set(value) {
        this.$emit('update:column', value)
      }
    },
    localOpen: {
      get() {
        return this.open
      },
      set(value) {
        this.$emit('update:open', value)
      }
    },
    widthValue: {
      get() {
        if (typeof this.column?.visual?.width === 'number') {
          return this.column.visual.width
        }

        return this.currentWidth
      },
      set(value) {
        value = value < 50 ? 50 : value

        this.$emit('update:column', {
          ...this.column,
          visual: {
            ...this.column?.visual,
            width: value
          }
        })
      }
    },
    localAvailableFields() {
      return Object.values(this.availableFields || {}).map((v) => ({
        ...v,
        id: v.id || v.attribute,
        name: v.name || v.label
      }))
    }
  },
  watch: {
    widthValue(newVal, oldVal) {
      if (oldVal) {
        if (this.widthAuto) {
          this.widthAuto = false
        }
      }
    },
    widthAuto(newVal, oldVal) {
      if (typeof oldVal === 'boolean') {
        if (newVal) {
          this.localColumn = {
            ...this.column,
            visual: {
              ...this.column?.visual,
              width: 'auto'
            }
          }
        }
      }
    },
    localOpen(newVal) {
      if (newVal) {
        this.oldLabel = {
          ...this.column?.label
        }

        if (this.toDelete) {
          this.toDelete = false
        }

        this.widthAuto = this.column?.visual?.width === 'auto'

        this.oldWidth = this.column?.visual?.width || 'auto'

        if (
          this.column &&
          this.column.type === 'progressbar' &&
          !Array.isArray(this.column.field)
        ) {
          this.column.field = this.column.field
            ? [this.column.field]
            : ['ALL']
        }
      }
    }
  },
  mounted() {},
  methods: {
    onFieldChange(value) {
      // Array values only come from a progressbar column's multi-select (several
      // category/group ids at once); auto-renaming the column label to one selected
      // field's name doesn't make sense there, and tasks_progress already carries every
      // atomic id's percentages, so no reload is needed for the new selection to render.
      if (Array.isArray(value)) {
        return
      }

      const availableField = this.localAvailableFields.find(
        (af) => af.id === value
      )

      if (availableField) {
        if (!this.reloadDossier) {
          this.reloadDossier = true
        }

        if (this.localColumn.label instanceof Object) {
          for (let key in this.localColumn.label) {
            this.localColumn.label[key] = availableField.name
          }
        }
      }
    },
    saveData() {
      this.$emit('update:dashboard', this.reloadDossier)
      this.localOpen = false

      if (this.reloadDossier) {
        this.reloadDossier = false
      }
    },
    deleteColumn() {
      if (this.toDelete) {
        this.$emit('delete:column')
        this.localOpen = false
      } else {
        this.toDelete = true
      }
    },
    cancelValue() {
      this.localColumn = {
        ...this.column,
        label: { ...this.oldLabel },
        visual: {
          ...this.column?.visual,
          width: this.oldWidth
        }
      }

      this.localOpen = false

      if (this.reloadDossier) {
        this.$emit('update:dashboard', true)

        this.reloadDossier = false
      }
    }
  }
}
</script>
